import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {ReturnDeliveryDialog} from "./ReturnDeliveryDialog";
import {BoxAccess, PushButton, useAsyncCallback, useBoxes, usePlatform} from "@variocube/cube-ui";
import {logger} from "./logger";
import {Delivery, DeliveryState, ReasonForReturnEnum, Recipient, ReturnDeliveryConfig} from "./types";
import {StorageFlow} from "./StorageFlow";
import {AccessCodeAlphabet, Occupancy} from "@variocube/app-api";
import {useLocalization} from "../i18n";
import {ReturnDeliveryVerify} from "./ReturnDeliveryVerify";
import {AuthorizeUserDialog} from "./AuthorizeUserDialog";
import { v4 as uuidv4 } from 'uuid';

enum Flow {
    Authorize,
    Dialog,
    Verify,
    Storage,
}

interface ReturnDeliveryFlowProps {
    delivery?: Delivery,
    occupancies?: Occupancy[],
    config: ReturnDeliveryConfig;
	onConfirm: () => void;
    onBack: () => void;
}

const log = logger.sub("ReturnDeliveryFlow");

export function ReturnDeliveryFlow({delivery: pDelivery, occupancies: pOccupancies, onConfirm, onBack, config}: ReturnDeliveryFlowProps) {

    const platform = usePlatform();
    const allBoxes = useBoxes();
    const {t} = useLocalization();

    const [flow, setFlow] = useState<Flow>(Flow.Authorize);
    const [delivery, setDelivery] = useState<Delivery>({
        bookmark: '',
        delivery: {
            deliveryId: uuidv4(),
            isCreatedOnCube: true,
            state: DeliveryState.Returned,
        },
        recipients: [],
        storage: {},
        returnDelivery: { description: '', state: DeliveryState.Stored}
    });
    const [occupancies, setOccupancies] = useState<Occupancy[]>([]);

    const [verifyPhone, setVerifyPhone] = useState<string | undefined>();
    const [verifyEmail, setVerifyEmail] = useState<string | undefined>();

    const boxes = useMemo(() => allBoxes.filter(box => occupancies.find(occupancy => occupancy.boxNumber == box.number)), [allBoxes, occupancies]);
    const [authorizedRecipient, setAuthorizedRecipient] = useState<Recipient>();


    async function handleOnReturnDialogSubmit(deliveryNoteId: string, reasonForReturn: ReasonForReturnEnum | undefined, email: string, phone: string, customerId: string, positionNumbers: string, addInfo: string) {
       let returnDelivery: Delivery = {
           bookmark: '',
           delivery: {
               deliveryId: uuidv4(),
            isCreatedOnCube: true,
               state: DeliveryState.Returned,
           },
           recipients: [],
           storage: {},
           returnDelivery: {
               description: addInfo,
               state: DeliveryState.Stored,
               deliveryNoteId,
               reasonForReturn,
               customerId,
               positionNumbers
           }
       };
       if (config.returnRequiresAuthorization) {
            if (authorizedRecipient && returnDelivery.returnDelivery) {
                returnDelivery.sender = {
                    ...authorizedRecipient,
                    name: authorizedRecipient.recipientName
                }
            }
        }

       if (email && email.length || phone && phone.length) {
           let recipient: Recipient = {recipientName: "", email: email, phone: phone};
           returnDelivery.recipients =  [recipient];
       }
       setDelivery(returnDelivery);

       if (pDelivery && pOccupancies) {
           const occupancyList = [];
           for (let occupancy of pOccupancies) {
               try {
                   await platform.endOccupancy(occupancy.uuid, { gracePeriod: 0 });
               } catch (err) {
                   log.error('failed to end occupancy, return not cannot continue')
               }

               try {
                   const o = await platform.occupy({
                       boxNumber: occupancy.boxNumber,
                       content: {
                           deliveries: [returnDelivery],
                       },
					   accessCodeShape: {
						   alphabet: AccessCodeAlphabet.OneToNine,
						   length: 5,
					   }
                   });
                   occupancyList.push(o);
               } catch (err) {
                   log.error('failed to occupy occupancy', err)
               }
           }
           setOccupancies(occupancyList);
       }

       const params = config.parameters ?? {};
       if (params) {
           const verifyPhoneParam = params.phone?.verification ?? false;
           const verifyEmailParam = params.email?.verification ?? false;
           if (verifyPhoneParam && phone) {
               setFlow(Flow.Verify);
               setVerifyPhone(phone);
           }
           if (verifyEmailParam && email) {
               setFlow(Flow.Verify);
               setVerifyEmail(email);
           }
           if (verifyPhoneParam || verifyEmailParam) {
               return;
           }
       }

       setFlow(Flow.Storage);
   }

    const cancel = useAsyncCallback(async () => {
        await Promise.all(occupancies.map(occupancy => platform.cancelOccupancy(occupancy.uuid)));
        setOccupancies([]);
        setFlow(Flow.Dialog);
    }, [occupancies, platform]);

    const confirm = useAsyncCallback(async () => {
        for (const occupancy of occupancies) {
            log.info("Confirming occupancy", occupancy);
            await platform.confirmOccupancy(occupancy.uuid);
        }
        onConfirm();
    }, [occupancies, platform]);


    async function handleOnAuthorizeUserDialogSubmit(recipient: Recipient|undefined) {
        if (recipient) { 
            setAuthorizedRecipient(recipient);
            setFlow(Flow.Dialog);
        } else {
            setAuthorizedRecipient(undefined);
            setFlow(Flow.Authorize);
        }   
    }
 
    useEffect(() => {
        if (flow === Flow.Authorize) { 
            console.log('check if return delivery needs authentication');
            // check if return delivery needs authentication
            if (!config.returnRequiresAuthorization) {
                setFlow(Flow.Dialog);
            }
        }
    }, [config]);

    if (flow === Flow.Authorize) { 
        return (
            <AuthorizeUserDialog
                authorizationProviderHint={config.authorizationProviderHint} 
                onBack={onBack}
                onSubmit={handleOnAuthorizeUserDialogSubmit}
            />
        );
    } 
    else if (flow === Flow.Dialog) { 
        return (
            <ReturnDeliveryDialog
                config={config}
                delivery={pDelivery}
                onBack={onBack}
                onSubmit={handleOnReturnDialogSubmit}
            />
        );
    } else if (flow == Flow.Verify) {
        return (
            <ReturnDeliveryVerify
                verifyPhone={verifyPhone}
                verifyEmail={verifyEmail}
                onNext={() => setFlow(Flow.Storage)}
                onBack={onBack}
            />
        );
    } else if (flow == Flow.Storage) {
        if (boxes.length) {
            const boxNames = boxes.map(box => box.number).join(", ");
            return (
                <BoxAccess
                    platform={platform}
                    boxes={boxes}
                    onCancel={cancel.execute}
                    onConfirm={confirm.execute}
                    strings={t}
                    boxOpenTitle={t("DeliveryStoreTitle")}
                    boxOpenMessage={t("DeliveryStoreMessage", {boxNames})}
                    confirmTitle={t("DeliveryConfirmTitle")}
                    confirmMessage={t("DeliveryConfirmMessage", {boxNames})}
                    openActions={
                        <PushButton onClick={cancel.execute}>
                            {t("Cancel")}
                        </PushButton>
                    }
                />
            );
        }

        return (
            <StorageFlow
                delivery={delivery}
                onDone={onBack}
            />
        )
    }
    return null;
}
